import dayjs from "dayjs";
import { Dialog } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import Constants from "common/constant";
import { useScale } from "common/utils";
import { ParentCloseBtn, FadeTransition } from "common/elements";
import i18n from "features/shared/i18n";
import { getPieceComments } from "features/player/api";
import { ParentPiece, ParentPieceCommentItem } from "features/player/types";

const statusChip = (piece: ParentPiece) => {
  if (piece.banned) {
    return {
      labelKey: "MSG_PARENT_PAGE_PLAYER_PIECE_BLOCKED",
      icon: "block",
      className: "bg-red-100/10 text-red-100",
    };
  }
  return piece.published
    ? {
        labelKey: "MSG_PARENT_PAGE_PLAYER_PIECE_PUBLISHED",
        icon: "public",
        className: "bg-green-60/20 text-green-100",
      }
    : {
        labelKey: "MSG_PARENT_PAGE_PLAYER_PIECE_UNPUBLISHED",
        icon: "lock",
        className: "bg-gray2-20 text-gray2-100",
      };
};

export const PiecePreviewModal = ({
  piece,
  onClose,
}: {
  piece: ParentPiece;
  onClose: () => void;
}) => {
  const { scale } = useScale();
  const chip = statusChip(piece);

  // このピースに付いたコメント。誰が何を書いたのか保護者が確かめられるようにする
  const { data: comments = [] } = useQuery<ParentPieceCommentItem[]>({
    queryKey: ["parent/getPieceComments", piece.piece_id],
    queryFn: () => getPieceComments(piece.piece_id),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <Dialog
      fullScreen
      open={true}
      maxWidth={false}
      sx={{ zIndex: 99999 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
    >
      <div className="flex-col-center w-full h-full bg-textcolor-black/50">
        <div style={{ transform: `scale(${scale})` }}>
          <div className="flex-col-el justify-start w-[760px] max-h-[760px] relative overflow-hidden border-solid border-gray2-30 border-[16px] rounded-[16px] bg-gray2-30">
            {/* スクロールバーと重ならないよう、少し内側に固定する */}
            <div className="flex-col-el absolute top-0 right-4 w-14 h-14 z-[110]">
              <ParentCloseBtn close={onClose} />
            </div>
            <div className="flex-col-el justify-start w-full flex-1 min-h-0 relative p-6 rounded-[8px] bg-white overflow-y-auto visible-scrollbar">
              <div className="flex-col-center w-full mb-4">
                <div className="flex-col-el flex-center w-[360px] h-[190px] rounded-[16px] overflow-hidden border-solid border-gray2-30 border-[4px] box-border bg-gray2-20">
                  {piece.capture ? (
                    <img
                      alt="ピースのキャプチャー"
                      className="w-full h-full object-contain"
                      src={piece.capture}
                      onError={(e) =>
                        ((e.target as HTMLImageElement).src =
                          `${Constants.assetHost}/assets/images/default_capture.png`)
                      }
                    />
                  ) : (
                    <span className="material-symbols-outlined text-[60px] text-gray2-100">
                      extension
                    </span>
                  )}
                </div>
              </div>

              <div className="flex-col-el gap-3">
                <h2 className="text-[36px] text-textcolor-black">{piece.name}</h2>

                {piece.description && (
                  <p className="text-[21px] text-gray2-100">{piece.description}</p>
                )}

                <div className="flex-row-el items-center flex-wrap gap-6 text-[21px] whitespace-nowrap">
                  <span
                    className={`flex-row-el items-center shrink-0 gap-1 px-3 py-1 rounded-full text-[19px] ${chip.className}`}
                  >
                    <span className="material-symbols-outlined text-[21px]">{chip.icon}</span>
                    {i18n.t(chip.labelKey)}
                  </span>
                  <span className="flex-row-el items-center shrink-0 gap-1 text-green-100">
                    <span className="material-symbols-outlined text-[24px]">play_arrow</span>
                    {piece.played}
                  </span>
                  <span className="flex-row-el items-center shrink-0 gap-1 text-red-100">
                    <span className="material-symbols-outlined text-[24px]">favorite</span>
                    {piece.liked}
                  </span>
                  <span className="flex-row-el items-center shrink-0 gap-1 text-blue-100">
                    <span className="material-symbols-outlined text-[24px]">chat_bubble</span>
                    {piece.commented}
                  </span>
                  <span className="flex-row-el items-center shrink-0 gap-1 text-gray2-100">
                    <span className="material-symbols-outlined text-[24px]">visibility</span>
                    {piece.viewed}
                  </span>
                </div>

                <div className="flex-col-el gap-2 pt-2 border-t border-outline">
                  <div className="flex-row-el items-center gap-2 text-[21px] text-textcolor-black">
                    <span className="material-symbols-outlined text-[24px] text-blue-100">
                      chat_bubble
                    </span>
                    {i18n.t("MSG_PARENT_PAGE_PLAYER_PIECE_COMMENTS_ON_PIECE")}
                    <span className="text-[19px] text-gray2-100">({comments.length})</span>
                  </div>

                  {comments.length > 0 ? (
                    <div className="flex-col-el gap-2">
                      {comments.map((comment) => (
                        <div
                          key={comment.id}
                          className="flex-col-el w-full gap-1 p-3 rounded-[12px] bg-gray2-20 box-border"
                        >
                          <div className="flex-row-el items-center justify-between w-full gap-2">
                            <span className="text-[19px] text-textcolor-black">
                              {comment.author_name ?? ""}
                            </span>
                            <span className="text-[18px] text-gray2-100 shrink-0">
                              {dayjs(comment.created_at).format("YYYY-MM-DD HH:mm")}
                            </span>
                          </div>
                          <p className="text-[21px] text-textcolor-black whitespace-pre-wrap break-all">
                            {comment.content}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-[19px] text-gray2-100">
                      {i18n.t("MSG_PARENT_PAGE_PLAYER_PIECE_COMMENTS_EMPTY")}
                    </span>
                  )}
                </div>

                <div className="flex-row-el items-center flex-wrap gap-4 text-[19px] text-gray2-100 pt-2 border-t border-outline whitespace-nowrap">
                  <span>
                    {i18n.t("MSG_PARENT_PAGE_PLAYER_PROJECT_CREATED")}:{" "}
                    {dayjs(piece.created_at).format("YYYY-MM-DD HH:mm")}
                  </span>
                  <span>
                    {i18n.t("MSG_PARENT_PAGE_PLAYER_PROJECT_UPDATED")}:{" "}
                    {dayjs(piece.updated_at).format("YYYY-MM-DD HH:mm")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
